import classNames from 'classnames';

import CloseIcon from '../../assets/icons/close.svg?react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FieldError } from 'react-hook-form';
import { IMaskInput } from 'react-imask';

import { PeopleApi, UserDTO } from '../../api';
import {
  useAuthContext,
  useLegalMutationsAllowed,
  useProfile,
} from '../../hooks';
import { User } from '../../model';
import type { AddressResponse } from '../../model/Address';
import { isoToRuDate, ruToIsoDate, toBirthdateInputValue } from '../../utils';
import { AddressAutocomplete, Modal } from '../molecules';

type ClientProfileFormModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  mode?: 'edit' | 'complete';
};

const COMPLETE_SUBTITLE =
  'Пожалуйста, введите свои данные для формирования и оплаты заказа.';

const COMPLETE_REQUIRED_HINT = 'Все поля обязательны для заполнения.';

type CompleteFieldErrors = {
  name?: string;
  surname?: string;
  midname?: string;
  birthdate?: string;
  address?: string;
};

const fieldClassEdit =
  'h-14 w-full rounded-xl border border-brand-2 px-5 py-[18px] text-base leading-5 text-brand-1 placeholder:text-brand-1/50 outline-none transition-colors focus:border-[#0788f1] focus:ring-0';

const fieldClassComplete =
  '!border-0 h-14 w-full rounded-xl bg-white px-5 py-[18px] text-base leading-5 text-[#151515] outline outline-1 outline-offset-[-1px] outline-[#afc4d1] transition-[outline] placeholder:text-[#151515]/50 focus:outline focus:outline-1 focus:outline-[#0788f1] focus:ring-0';

const mapProfileAddress = (profile: User): AddressResponse | null => {
  const first = profile.address?.[0];
  if (!first?.address_text) {
    return null;
  }
  return {
    id: first.id,
    address_text: first.address_text,
    people_main_id: profile.people_main_id,
  };
};

const ClientProfileFormModal = ({
  isOpen,
  onClose,
  title,
  mode = 'edit',
}: ClientProfileFormModalProps) => {
  const profile = useProfile(true);
  const { allowed: legalMutationsAllowed, requireConsent } =
    useLegalMutationsAllowed();
  const { markClientProfileComplete } = useAuthContext();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedAddress, setSelectedAddress] =
    useState<AddressResponse | null>(null);
  const [completeFieldErrors, setCompleteFieldErrors] =
    useState<CompleteFieldErrors>({});

  const isComplete = mode === 'complete';
  const fieldClassName = isComplete ? fieldClassComplete : fieldClassEdit;

  const { mutate, isLoading } = useMutation({
    mutationFn: (data: UserDTO & { avatarFile?: File }) =>
      PeopleApi.updateMe(data),
    onSuccess: () => {
      markClientProfileComplete();
      queryClient.refetchQueries({ queryKey: [PeopleApi.KEY, 'me'] });
      onClose();
      setAvatarPreview(null);
      setSelectedFile(null);
    },
  });

  const [formData, setFormData] = useState<UserDTO>({
    name: '',
    surname: '',
    midname: '',
    birthdate: '',
    address_text: '',
  });

  useEffect(() => {
    if (profile && isOpen) {
      setFormData({
        name: profile.name || '',
        surname: profile.surname || '',
        midname: profile.midname || '',
        birthdate: isoToRuDate(toBirthdateInputValue(profile.birthdate)),
        address_text: profile.address?.[0]?.address_text || '',
      });
      setSelectedAddress(mapProfileAddress(profile));
      setAvatarPreview(null);
      setSelectedFile(null);
      setCompleteFieldErrors({});
    }
  }, [profile, isOpen]);

  const clearCompleteError = useCallback((field: keyof CompleteFieldErrors) => {
    setCompleteFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleAvatarClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
          setAvatarPreview(reader.result as string);
        };
        reader.readAsDataURL(file);
      }
    },
    [],
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!legalMutationsAllowed) {
        requireConsent();
        return;
      }
      const addressText =
        selectedAddress?.address_text?.trim() ||
        formData.address_text?.trim() ||
        '';

      const birthdateRu = formData.birthdate?.trim() || '';
      const birthdateIso = birthdateRu ? ruToIsoDate(birthdateRu) : null;

      const nextErrors: CompleteFieldErrors = {};
      if (birthdateRu && !birthdateIso) {
        nextErrors.birthdate = 'Укажите корректную дату рождения';
      }
      if (isComplete) {
        if (!formData.name?.trim()) {
          nextErrors.name = 'Укажите имя';
        }
        if (!formData.surname?.trim()) {
          nextErrors.surname = 'Укажите фамилию';
        }
        if (!formData.midname?.trim()) {
          nextErrors.midname = 'Укажите отчество';
        }
        if (!birthdateRu) {
          nextErrors.birthdate = 'Укажите дату рождения';
        }
        if (!addressText) {
          nextErrors.address = 'Укажите адрес';
        }
      }
      if (Object.keys(nextErrors).length > 0) {
        setCompleteFieldErrors(nextErrors);
        return;
      }
      setCompleteFieldErrors({});

      mutate({
        ...formData,
        address_text: addressText || undefined,
        birthdate: birthdateIso || undefined,
        avatarFile: selectedFile || undefined,
      });
    },
    [
      formData,
      selectedFile,
      selectedAddress,
      mutate,
      isComplete,
      legalMutationsAllowed,
      requireConsent,
    ],
  );

  const currentAvatar = avatarPreview || profile?.avatar;
  const photoActionLabel = currentAvatar ? 'Изменить фото' : 'Загрузить фото';

  const addressFieldError: FieldError | undefined = completeFieldErrors.address
    ? { type: 'required', message: completeFieldErrors.address }
    : undefined;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      backdropClassName="bg-black/20"
      showCloseButton={false}
      panelClassName="max-h-[min(100vh,860px)] !max-w-[468px] w-full !rounded-3xl shadow-none md:max-h-[calc(100vh-40px)]"
    >
      <form
        onSubmit={handleSubmit}
        className={classNames(
          'flex flex-col bg-white',
          isComplete ? 'rounded-2xl' : 'gap-8 rounded-3xl p-6',
        )}
      >
        <div
          className={classNames(
            'flex w-full items-center gap-8',
            isComplete ? 'px-6 pt-6' : '',
          )}
        >
          <h2 className="flex-1 text-2xl font-medium leading-8 text-[#151515]">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex size-6 shrink-0 items-center justify-center opacity-50 transition-opacity hover:opacity-100"
            aria-label="Закрыть"
          >
            <CloseIcon className="size-6" />
          </button>
        </div>

        <div
          className={classNames(
            'flex w-full flex-col',
            isComplete ? 'px-6 pt-2 pb-6' : 'gap-8',
          )}
        >
          {isComplete ? (
            <div className="mx-auto flex w-full max-w-[420px] flex-col gap-8">
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <p className="text-base font-normal leading-6 text-[#8e8e93]">
                    {COMPLETE_SUBTITLE}
                  </p>
                  <p className="text-sm leading-5 text-[#8e8e93]">
                    {COMPLETE_REQUIRED_HINT}
                  </p>
                </div>
                <div className="flex flex-col gap-4">
                  <div className="flex w-full flex-col gap-1">
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        clearCompleteError('name');
                        setFormData({ ...formData, name: e.target.value });
                      }}
                      className={classNames(
                        fieldClassComplete,
                        completeFieldErrors.name &&
                          '!outline !outline-1 !outline-[red]',
                      )}
                      placeholder="Имя"
                      autoComplete="given-name"
                    />
                    {completeFieldErrors.name ? (
                      <p className="text-sm text-red">
                        {completeFieldErrors.name}
                      </p>
                    ) : null}
                  </div>

                  <div className="flex w-full flex-col gap-1">
                    <input
                      type="text"
                      value={formData.surname}
                      onChange={(e) => {
                        clearCompleteError('surname');
                        setFormData({ ...formData, surname: e.target.value });
                      }}
                      className={classNames(
                        fieldClassComplete,
                        completeFieldErrors.surname &&
                          '!outline !outline-1 !outline-[red]',
                      )}
                      placeholder="Фамилия"
                      autoComplete="family-name"
                    />
                    {completeFieldErrors.surname ? (
                      <p className="text-sm text-red">
                        {completeFieldErrors.surname}
                      </p>
                    ) : null}
                  </div>

                  <div className="flex w-full flex-col gap-1">
                    <input
                      type="text"
                      value={formData.midname}
                      onChange={(e) => {
                        clearCompleteError('midname');
                        setFormData({ ...formData, midname: e.target.value });
                      }}
                      className={classNames(
                        fieldClassComplete,
                        completeFieldErrors.midname &&
                          '!outline !outline-1 !outline-[red]',
                      )}
                      placeholder="Отчество"
                      autoComplete="additional-name"
                    />
                    {completeFieldErrors.midname ? (
                      <p className="text-sm text-red">
                        {completeFieldErrors.midname}
                      </p>
                    ) : null}
                  </div>

                  <div className="flex w-full flex-col gap-1">
                    <IMaskInput
                      mask="00.00.0000"
                      inputMode="numeric"
                      value={formData.birthdate ?? ''}
                      onAccept={(value: string) => {
                        clearCompleteError('birthdate');
                        setFormData((prev) => ({ ...prev, birthdate: value }));
                      }}
                      className={classNames(
                        fieldClassComplete,
                        completeFieldErrors.birthdate &&
                          '!outline !outline-1 !outline-[red]',
                      )}
                      placeholder="Дата рождения (ДД.ММ.ГГГГ)"
                    />
                    {completeFieldErrors.birthdate ? (
                      <p className="text-sm text-red">
                        {completeFieldErrors.birthdate}
                      </p>
                    ) : null}
                  </div>

                  <AddressAutocomplete
                    value={selectedAddress}
                    error={addressFieldError}
                    onChange={(addr) => {
                      clearCompleteError('address');
                      setSelectedAddress(addr);
                      setFormData((prev) => ({
                        ...prev,
                        address_text: addr.address_text || '',
                      }));
                    }}
                    inputClassName={fieldClassComplete}
                    placeholder="Адрес"
                  />
                </div>
              </div>

              <div className="flex w-full flex-col gap-4">
                <button
                  type="submit"
                  disabled={isLoading || !legalMutationsAllowed}
                  className="flex h-[60px] w-full items-center justify-center rounded-xl bg-[#0788f1] px-5 text-center text-base font-bold leading-5 text-white transition-opacity disabled:opacity-50"
                >
                  {isLoading ? 'Сохранение...' : 'Сохранить'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-[60px] w-full items-center justify-center rounded-xl bg-[#0788f1]/10 px-5 text-center text-base font-bold leading-5 text-[#0788f1]"
                >
                  Отмена
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex w-full flex-col gap-4">
                <div className="flex w-full items-center gap-5">
                  <button
                    type="button"
                    onClick={handleAvatarClick}
                    className="relative flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#0788f1]/10"
                  >
                    {currentAvatar ? (
                      <img
                        src={currentAvatar}
                        alt=""
                        className="size-full object-cover"
                      />
                    ) : (
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="shrink-0"
                        aria-hidden
                      >
                        <path
                          d="M10 12.5C12.7614 12.5 15 10.2614 15 7.5C15 4.73858 12.7614 2.5 10 2.5C7.23858 2.5 5 4.73858 5 7.5C5 10.2614 7.23858 12.5 10 12.5Z"
                          stroke="#0788F1"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M2.5 16.875C4.01328 14.2602 6.76172 12.5 10 12.5C13.2383 12.5 15.9867 14.2602 17.5 16.875"
                          stroke="#0788F1"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleAvatarClick}
                    className="flex flex-1 flex-col items-start gap-1 text-left text-base font-normal leading-6 text-[#0788f1]"
                  >
                    {photoActionLabel}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>

                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className={fieldClassName}
                  placeholder="Имя"
                  autoComplete="given-name"
                />

                <input
                  type="text"
                  value={formData.surname}
                  onChange={(e) =>
                    setFormData({ ...formData, surname: e.target.value })
                  }
                  className={fieldClassName}
                  placeholder="Фамилия"
                  autoComplete="family-name"
                />

                <input
                  type="text"
                  value={formData.midname}
                  onChange={(e) =>
                    setFormData({ ...formData, midname: e.target.value })
                  }
                  className={fieldClassName}
                  placeholder="Отчество"
                  autoComplete="additional-name"
                />

                <div className="flex w-full flex-col gap-1">
                  <span className="text-sm leading-5 text-brand-1/60">
                    Дата рождения
                  </span>
                  <IMaskInput
                    mask="00.00.0000"
                    inputMode="numeric"
                    value={formData.birthdate ?? ''}
                    onAccept={(value: string) => {
                      clearCompleteError('birthdate');
                      setFormData((prev) => ({ ...prev, birthdate: value }));
                    }}
                    className={classNames(
                      fieldClassName,
                      'text-brand-1',
                      completeFieldErrors.birthdate && '!border-[red]',
                    )}
                    placeholder="ДД.ММ.ГГГГ"
                  />
                  {completeFieldErrors.birthdate ? (
                    <p className="text-sm text-red">
                      {completeFieldErrors.birthdate}
                    </p>
                  ) : null}
                </div>

                <AddressAutocomplete
                  value={selectedAddress}
                  onChange={(addr) => {
                    setSelectedAddress(addr);
                    setFormData((prev) => ({
                      ...prev,
                      address_text: addr.address_text || '',
                    }));
                  }}
                  inputClassName={fieldClassName}
                  placeholder="Адрес (необязательно)"
                />
              </div>

              <div className="flex w-full flex-col gap-4">
                <button
                  type="submit"
                  disabled={isLoading || !legalMutationsAllowed}
                  className="flex h-[60px] w-full items-center justify-center rounded-xl bg-[#0788f1] px-5 text-center text-base font-bold leading-5 text-white transition-opacity disabled:opacity-50"
                >
                  {isLoading ? 'Сохранение...' : 'Сохранить'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex h-[60px] w-full items-center justify-center rounded-xl bg-[#0788f1]/10 px-5 text-center text-base font-bold leading-5 text-[#0788f1]"
                >
                  Отмена
                </button>
              </div>
            </>
          )}
        </div>
      </form>
    </Modal>
  );
};

type ProfileModalShellProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const EditProfileModal = (props: ProfileModalShellProps) => (
  <ClientProfileFormModal
    {...props}
    title="Редактировать профиль"
    mode="edit"
  />
);

export const CompleteProfileModal = (props: ProfileModalShellProps) => (
  <ClientProfileFormModal
    {...props}
    title="Заполните профиль"
    mode="complete"
  />
);
